import { publicContactPoint } from "@/lib/content/publicContact";
import { consultingPrices } from "@/lib/offers/consulting-pricing";
import { starterKitFallback } from "@/lib/offers/fixed-offers";
import { websitePackageAmounts } from "@/lib/offers/local-pricing";
import { scanOptions } from "@/lib/offers/scan-pricing";
import {
  andrewIdentity,
  businessJsonLdId,
  defaultOgImage,
  personJsonLdId,
  servBuilderOrgId,
} from "@/lib/seo/identity";
import { siteBaseUrl } from "@/utils/baseUrl";

const ORIGIN = siteBaseUrl.origin;

/** Keep meta descriptions inside the length Google actually shows. */
export function clampMetaDescription(value: string, max = 155): string {
  const text = value.replace(/\s+/g, " ").trim();
  if (text.length <= max) return text;

  const window = text.slice(0, max + 1);
  const sentenceEnd = Math.max(
    window.lastIndexOf(". "),
    window.lastIndexOf("! "),
    window.lastIndexOf("? "),
  );
  if (sentenceEnd >= 80) return text.slice(0, sentenceEnd + 1);

  const sliced = text.slice(0, max);
  const cut = sliced.lastIndexOf(" ");
  let base = (cut > 80 ? sliced.slice(0, cut) : sliced).replace(/[.,;:\s-]+$/, "");
  base = base.replace(/\b(and|or|with|for|the|a|an|to|of|in)$/i, "").replace(/[.,;:\s-]+$/, "");
  return `${base}.`;
}

/** Offer node for a local package. Monthly care uses a one-month billing duration. */
export function localPackageOfferJsonLd(pkg: {
  name: string;
  amount: number;
  period: "one-time" | "monthly" | string;
}): Record<string, unknown> {
  if (pkg.period === "monthly") {
    return {
      "@type": "Offer",
      name: pkg.name,
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: pkg.amount,
        priceCurrency: "USD",
        billingDuration: "P1M",
      },
    };
  }

  return {
    "@type": "Offer",
    name: pkg.name,
    price: pkg.amount,
    priceCurrency: "USD",
  };
}

export function breadcrumbJsonLd(items: { name: string; path: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: new URL(item.path, siteBaseUrl).toString(),
    })),
  };
}

export function personReference() {
  return {
    "@type": "Person",
    "@id": personJsonLdId,
    name: andrewIdentity.name,
    url: andrewIdentity.url,
  };
}

export function faqJsonLd(questions: { question: string; answer: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: questions.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
}

/**
 * Person node. `founder` is omitted: schema.org puts that property on
 * Organization, and the ServBuilder organization points back here.
 */
export function personEntity() {
  return {
    "@type": "Person",
    "@id": personJsonLdId,
    name: andrewIdentity.name,
    url: andrewIdentity.url,
    image: defaultOgImage,
    jobTitle: andrewIdentity.jobTitle,
    description: andrewIdentity.description,
    worksFor: { "@id": servBuilderOrgId },
    homeLocation: {
      "@type": "Place",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Omaha",
        addressRegion: "NE",
        addressCountry: "US",
      },
    },
    alumniOf: [
      { "@type": "CollegeOrUniversity", name: "Western Governors University" },
      { "@type": "CollegeOrUniversity", name: "Kirkwood Community College" },
    ],
    hasOccupation: {
      "@type": "Occupation",
      name: "Engineering Leader and AI Developer",
      occupationLocation: { "@type": "City", name: "Omaha, NE" },
    },
    knowsAbout: [...andrewIdentity.knowsAbout],
    sameAs: [...andrewIdentity.sameAs],
  };
}

export function personJsonLd() {
  return {
    "@context": "https://schema.org",
    ...personEntity(),
  };
}

export function servBuilderOrganizationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": servBuilderOrgId,
    name: "ServBuilder",
    url: "https://www.servbuilder.com",
    description:
      "Field service management software for small service businesses, with AI-assisted quoting and scheduling.",
    founder: { "@id": personJsonLdId },
  };
}

const IN_STOCK = "https://schema.org/InStock";

export type ServiceOfferInput = {
  name: string;
  description: string;
  url: string;
  price?: number;
  minPrice?: number;
  maxPrice?: number;
  /** MON is a month. HUR is an hour. Omit for a one-time price. */
  unitCode?: "MON" | "HUR";
};

export function moneyOffer(input: ServiceOfferInput) {
  const priceSpecification: Record<string, unknown> = {
    "@type": input.unitCode ? "UnitPriceSpecification" : "PriceSpecification",
    priceCurrency: "USD",
  };
  if (typeof input.price === "number") priceSpecification.price = input.price;
  if (typeof input.minPrice === "number") priceSpecification.minPrice = input.minPrice;
  if (typeof input.maxPrice === "number") priceSpecification.maxPrice = input.maxPrice;
  if (input.unitCode) priceSpecification.unitCode = input.unitCode;
  if (input.unitCode === "MON") priceSpecification.billingDuration = "P1M";

  return {
    "@type": "Offer" as const,
    name: input.name,
    description: input.description,
    url: input.url,
    priceCurrency: "USD",
    availability: IN_STOCK,
    ...(typeof input.price === "number" ? { price: input.price } : {}),
    priceSpecification,
  };
}

export function serviceJsonLd(input: {
  id: string;
  name: string;
  serviceType: string;
  description: string;
  path: string;
  offers?: ServiceOfferInput[];
}) {
  const offers = (input.offers ?? []).map((offer) => moneyOffer(offer));
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": input.id,
    name: input.name,
    serviceType: input.serviceType,
    description: input.description,
    url: new URL(input.path, siteBaseUrl).toString(),
    provider: { "@id": businessJsonLdId },
    areaServed: { "@type": "Country", name: "United States" },
    ...(offers.length > 0 ? { offers } : {}),
  };
}

function catalogOffer(
  name: string,
  description: string,
  path: string,
  price: { price?: number; minPrice?: number; maxPrice?: number; unitCode?: "MON" | "HUR" },
) {
  return moneyOffer({
    name,
    description,
    url: new URL(path, siteBaseUrl).toString(),
    ...price,
  });
}

/**
 * Public offer catalog. The 60-minute consult has a page price, and that
 * amount is left out of JSON-LD on purpose.
 */
export function professionalServiceJsonLd() {
  const leadership = "/consulting#fractional-leadership";
  const websites = "/consulting#website-builds";
  const builds = "/consulting#software-ai";
  const starterKitPrice = starterKitFallback.amount;

  return {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": businessJsonLdId,
    name: "Andrew Leger",
    legalName: "AJLDev, LLC",
    url: `${ORIGIN}/consulting`,
    description:
      "AI consulting and development for small companies through AJLDev, LLC in Omaha, NE: fractional engineering and AI leadership, websites with AI features, and custom LLM apps, agents, automations, and integrations.",
    knowsAbout: ["AI consulting", "AI automation", "LLM applications", "AI agents", "Engineering leadership"],
    founder: { "@id": personJsonLdId },
    employee: { "@id": personJsonLdId },
    image: defaultOgImage,
    address: {
      "@type": "PostalAddress",
      addressLocality: "Omaha",
      addressRegion: "NE",
      addressCountry: "US",
    },
    areaServed: [
      { "@type": "City", name: "Omaha" },
      { "@type": "Country", name: "United States" },
    ],
    contactPoint: publicContactPoint(),
    priceRange: "$29 to $12,000+",
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Services",
      itemListElement: [
        catalogOffer(
          "AI SDLC Assessment",
          "A two-week assessment of how a team uses AI in delivery, credited toward month one of a retainer.",
          leadership,
          { price: consultingPrices.aiSdlcAssessment },
        ),
        catalogOffer(
          "Fractional leadership, Advisor",
          "Part-time engineering and AI leadership, about 4 hours a week.",
          leadership,
          { price: consultingPrices.advisorMonthly, unitCode: "MON" },
        ),
        catalogOffer(
          "Fractional leadership, Core",
          "Part-time engineering and AI leadership, about 8 hours a week.",
          leadership,
          { price: consultingPrices.coreMonthly, unitCode: "MON" },
        ),
        catalogOffer(
          "Fractional leadership, Embedded",
          "Part-time engineering and AI leadership with more hours in the team each week.",
          leadership,
          { price: consultingPrices.embeddedMonthly, unitCode: "MON" },
        ),
        catalogOffer(
          "Starter site",
          "A website of up to 5 pages. Half is due to start and half at launch.",
          websites,
          { price: websitePackageAmounts.starter },
        ),
        catalogOffer(
          "Pro site",
          "A website of up to 12 pages, with an editor, analytics, and local SEO.",
          websites,
          { price: websitePackageAmounts.pro },
        ),
        catalogOffer(
          "AI assistant add-on",
          "An AI assistant trained on your site content.",
          websites,
          { price: consultingPrices.aiAssistant },
        ),
        catalogOffer(
          "Smart search add-on",
          "Search over your site content.",
          websites,
          { price: consultingPrices.smartSearch },
        ),
        catalogOffer(
          "Lead automation add-on",
          "Automation that follows up on new leads from the site.",
          websites,
          { price: consultingPrices.leadAutomation },
        ),
        catalogOffer(
          "AI-ready bundle",
          "Pro site plus the AI assistant and lead automation.",
          websites,
          { price: websitePackageAmounts.aiReadyBundle },
        ),
        catalogOffer(
          "Care",
          "Monthly hosting, updates, small edits, and uptime monitoring.",
          websites,
          { price: websitePackageAmounts.careMonthly, unitCode: "MON" },
        ),
        catalogOffer(
          "Care + AI",
          "The Care plan, plus monitoring and tuning for the site's AI chat or automations.",
          websites,
          { price: websitePackageAmounts.careAiMonthly, unitCode: "MON" },
        ),
        catalogOffer(
          "AI Starter chat",
          `Website AI chat, up to ${consultingPrices.aiStarterChats} chats a month.`,
          websites,
          { price: consultingPrices.aiStarterMonthly, unitCode: "MON" },
        ),
        catalogOffer(
          "AI Standard chat",
          `Website AI chat, up to ${consultingPrices.aiStandardChats} chats a month.`,
          websites,
          { price: consultingPrices.aiStandardMonthly, unitCode: "MON" },
        ),
        catalogOffer(
          "AI Pro chat",
          `Website AI chat, up to ${consultingPrices.aiProChats.toLocaleString("en-US")} chats a month.`,
          websites,
          { price: consultingPrices.aiProMonthly, unitCode: "MON" },
        ),
        catalogOffer(
          "AI Quick-Start Audit",
          "A one-week audit of one workflow, credited toward the build.",
          builds,
          { price: consultingPrices.quickStartAudit },
        ),
        catalogOffer(
          "Single automation",
          "One automation, scoped and quoted before work starts.",
          builds,
          { minPrice: consultingPrices.automationMin, maxPrice: consultingPrices.automationMax },
        ),
        catalogOffer(
          "AI assistant or LLM app",
          "A custom assistant or LLM application, with source code handed over.",
          builds,
          { minPrice: consultingPrices.llmAppMin, maxPrice: consultingPrices.llmAppMax },
        ),
        catalogOffer(
          "Agent, multi-step system, or MVP",
          "An agent, a multi-step system, or an MVP. The price starts at the published minimum.",
          builds,
          { minPrice: consultingPrices.agentFrom },
        ),
        catalogOffer(
          "Build retainer",
          "About 40 hours a month, with a 2-month minimum.",
          builds,
          { price: consultingPrices.buildRetainerMonthly, unitCode: "MON" },
        ),
        catalogOffer(
          "Hourly",
          `Engineering time with a ${consultingPrices.hourlyMinimumHours}-hour minimum.`,
          builds,
          { price: consultingPrices.hourly, unitCode: "HUR" },
        ),
        catalogOffer(
          "Support",
          "Monthly support after a build.",
          builds,
          {
            minPrice: consultingPrices.supportMinMonthly,
            maxPrice: consultingPrices.supportMaxMonthly,
            unitCode: "MON",
          },
        ),
        ...scanOptions.map((option) =>
          catalogOffer(option.name, option.schemaDescription, "/scan#order", { price: option.amount }),
        ),
        ...(typeof starterKitPrice === "number"
          ? [
              catalogOffer(
                "Next.js starter kit",
                "A Next.js project with Clerk, Stripe, and Neon, ready to clone.",
                "/products",
                { price: starterKitPrice },
              ),
            ]
          : []),
      ],
    },
  };
}
